import {
  CRMChannel,
  CRMConversationAutoResponseStatus,
  TwilioMessageModel,
  WebChatBotMessageModel,
  WhatsappMessageModel,
} from "@jugl-web/rest-api";
import { InstagramMessageModel } from "@jugl-web/rest-api/integrations/models/InstagramMessage/InstagramMessageModel";
import { useTranslations } from "@jugl-web/utils";
import { ARIVU_SYSTEM_CHAT_USER_ID } from "@jugl-web/utils/consts";
import { ChatMessageBubbleContextValue } from "@web-src/modules/chats/components/CommonMessageBubble/providers/ChatMessageBubbleProvider/ChatMessageBubbleContext";
import { useCallback } from "react";
import { useClientSupportPageContext } from "../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { getConversationUsername } from "../../WebChatAvatar/utils";
import aiAgentIcon from "../assets/ai-agent.svg?url";
import instagramIcon from "../assets/instagram.svg?url";
import smsIcon from "../assets/sms.svg?url";
import updateIcon from "../assets/update.svg?url";
import whatsappIcon from "../assets/whatsapp.svg?url";

type SenderDetails = Pick<
  ChatMessageBubbleContextValue,
  "senderName" | "senderImage" | "senderAvatarName"
>;

export const useGetSenderDetails = () => {
  const { t } = useTranslations();
  const { selectedConversation } = useClientSupportPageContext();

  const getSenderDetails = useCallback(
    (
      message?:
        | WhatsappMessageModel
        | InstagramMessageModel
        | WebChatBotMessageModel
        | TwilioMessageModel,
      channel?: CRMChannel
    ): SenderDetails => {
      if (message?.from === ARIVU_SYSTEM_CHAT_USER_ID) {
        if (
          "auto_response" in message &&
          message.auto_response === CRMConversationAutoResponseStatus.active
        ) {
          return {
            senderName: t({
              id: "client-support-page.ai-agent",
              defaultMessage: "AI Agent",
            }),
            senderImage: aiAgentIcon,
          };
        }
        return {
          senderName: t({
            id: "client-support-page.jugl-task-update",
            defaultMessage: "Jugl Task Update",
          }),
          senderImage: updateIcon,
        };
      }
      if (message?.type === "incoming") {
        const conversationName =
          selectedConversation?.contact_info?.name ||
          selectedConversation?.name ||
          ("name" in message ? message.name : "");

        return {
          senderName: conversationName,
          senderImage:
            channel === CRMChannel.whatsapp
              ? whatsappIcon
              : channel === CRMChannel.instagram
                ? instagramIcon
                : channel === CRMChannel.twilio
                  ? smsIcon
                  : undefined,
          senderAvatarName:
            channel === CRMChannel.chatbot
              ? getConversationUsername(conversationName)
              : undefined,
        };
      }

      return {
        senderName: undefined,
        senderImage: undefined,
      };
    },
    [t, selectedConversation]
  );

  return { getSenderDetails };
};
